import { Component, DestroyRef, ElementRef, computed, inject, output, signal } from '@angular/core';

import { createFloatingPanelDismiss } from '../../shared/overlay/floating-panel-dismiss';
import type { FloatingPanelDismiss } from '../../shared/overlay/floating-panel-dismiss';

import type { InventoryUnitStatus } from '../inventory/models/inventory.model';

/**
 * Issue #63 — the multiselect groups the four unit statuses plus the
 * catalog-activity axis, which is never a unit status and stays last.
 */
type VisibilityOption =
  | { kind: 'status'; id: InventoryUnitStatus; label: string }
  | { kind: 'inactive'; id: 'INACTIVE'; label: string };

@Component({
  selector: 'app-stock-filters',
  standalone: true,
  template: `
    <div class="filters">
      <div class="stock-filters">
        <button
          type="button"
          class="stock-filters__trigger app-action app-action--secondary"
          (click)="onTriggerClick($event)"
        >
          Visibilidad ({{ visibleCount() }}/5)
        </button>

        @if (isOpen()) {
          <div
            class="stock-filters__panel stock-filters__panel--floating"
            [style.top.px]="position()?.top"
            [style.left.px]="position()?.left"
            (click)="$event.stopPropagation()"
          >
            @for (option of visibilityOptions; track option.id) {
              <label class="app-check-option">
                <input
                  type="checkbox"
                  [checked]="isOptionChecked(option)"
                  (change)="onOptionChange(option)"
                />
                <span>{{ option.label }}</span>
              </label>
            }
          </div>
        }
      </div>
    </div>
  `,
  styles: [
    `
      .filters {
        display: flex;
        align-items: center;
        gap: 16px;
        flex-wrap: wrap;
      }

      .stock-filters {
        position: relative;
        display: inline-block;
      }

      .stock-filters__panel--floating {
        display: flex;
        flex-direction: column;
        position: fixed;
        background-color: var(--surface-card, #fff);
        border: 1px solid var(--border-color, #ccc);
        border-radius: var(--radius-md, 4px);
        box-shadow: var(--shadow-md, 0 4px 6px rgba(0, 0, 0, 0.1));
        z-index: 100;
        padding: 0.5rem;
        gap: 0.25rem;
        min-width: 170px;
      }
      /* Option/checkbox styling lives in the shared .app-check-option rules
         in styles.css (openspec stock-toolbar-panel-dismiss, task 5.2). */
    `
  ]
})
export class StockFiltersComponent {
  private readonly elementRef = inject(ElementRef);
  private readonly destroyRef = inject(DestroyRef);

  /** Issue #63 — visibility options. Disponible is the only default; the fifth option is the product axis. */
  readonly checkedStatuses = signal<Set<InventoryUnitStatus>>(
    new Set<InventoryUnitStatus>(['AVAILABLE'])
  );
  readonly includeInactive = signal(false);
  readonly checkedStatusesChange = output<Set<InventoryUnitStatus>>();
  readonly includeInactiveChange = output<boolean>();
  readonly brandChanged = output<string>();
  readonly modelChanged = output<string>();
  readonly colorChanged = output<string>();
  readonly capacityChanged = output<string>();
  readonly supplierChanged = output<string>();
  readonly brand = signal('');
  readonly model = signal('');
  readonly color = signal('');
  readonly capacity = signal('');
  readonly supplierId = signal('');

  /** Issue #63 — one multiselect, the four unit statuses first and the product axis last. */
  readonly visibilityOptions: VisibilityOption[] = [
    { kind: 'status', id: 'AVAILABLE', label: 'Disponible' },
    { kind: 'status', id: 'IN_REPAIR', label: 'En reparación' },
    { kind: 'status', id: 'MISSING', label: 'Faltante' },
    { kind: 'status', id: 'SOLD', label: 'Vendido' },
    { kind: 'inactive', id: 'INACTIVE', label: 'Inactivos' }
  ];

  readonly isOpen = signal(false);
  readonly position = signal<{ top: number; left: number } | null>(null);
  readonly visibleCount = computed(() => this.checkedStatuses().size + (this.includeInactive() ? 1 : 0));

  private readonly dismiss: FloatingPanelDismiss;

  constructor() {
    this.dismiss = createFloatingPanelDismiss({
      getHost: () => this.elementRef.nativeElement,
      getPanel: () => this.elementRef.nativeElement.querySelector('.stock-filters__panel'),
      close: () => this.closePanel(),
    });
    // The helper owns no lifecycle of its own: if the component goes away while
    // the panel is open, the document listeners must go with it.
    this.destroyRef.onDestroy(() => this.detachDismissListeners());
  }

  isOptionChecked(option: VisibilityOption): boolean {
    return option.kind === 'inactive'
      ? this.includeInactive()
      : this.checkedStatuses().has(option.id);
  }

  onOptionChange(option: VisibilityOption): void {
    if (option.kind === 'inactive') {
      this.toggleIncludeInactive();
      return;
    }

    this.toggleStatus(option.id);
  }

  onTriggerClick(event: MouseEvent): void {
    event.stopPropagation();

    if (this.isOpen()) {
      this.closePanel();
      return;
    }

    const button = event.currentTarget as HTMLElement;
    const rect = button.getBoundingClientRect();
    this.position.set({ top: rect.bottom + 4, left: rect.left });
    this.isOpen.set(true);
    this.attachDismissListeners();
  }

  /** Close on a document `click` outside the host (public close helper). */
  onDocumentClick(event: MouseEvent): void {
    if (this.elementRef.nativeElement.contains(event.target as Node)) {
      return;
    }
    this.closePanel();
  }

  /** Close on Escape (public close helper). */
  onEscape(): void {
    this.closePanel();
  }

  private closePanel(): void {
    this.isOpen.set(false);
    this.detachDismissListeners();
  }

  private attachDismissListeners(): void {
    if (typeof document === 'undefined') {
      return; // DOM-less unit harness: there is no document to listen on.
    }
    this.dismiss.attach();
  }

  private detachDismissListeners(): void {
    if (typeof document === 'undefined') {
      return; // Nothing was ever attached in a DOM-less unit harness.
    }
    this.dismiss.detach();
  }

  toggleStatus(status: InventoryUnitStatus): void {
    const next = new Set(this.checkedStatuses());

    if (next.has(status)) {
      next.delete(status);
    } else {
      next.add(status);
    }

    this.checkedStatuses.set(next);
    this.checkedStatusesChange.emit(next);
  }

  toggleIncludeInactive(): void {
    const next = !this.includeInactive();
    this.includeInactive.set(next);
    this.includeInactiveChange.emit(next);
  }

  onBrandChange(value: string): void {
    this.brand.set(value);
    this.brandChanged.emit(value);
  }

  onModelChange(value: string): void {
    this.model.set(value);
    this.modelChanged.emit(value);
  }

  onColorChange(value: string): void {
    this.color.set(value);
    this.colorChanged.emit(value);
  }

  onCapacityChange(value: string): void {
    this.capacity.set(value);
    this.capacityChanged.emit(value);
  }

  onSupplierChange(value: string): void {
    this.supplierId.set(value);
    this.supplierChanged.emit(value);
  }
}
